import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useAdminOrders, useKitchenStations } from '../../lib/admin-hooks';
import { useTableManagement } from '../../lib/table-hooks';
import { PayTableModal } from '../../components/admin/PayTableModal';
import type { ActionResult } from '../../lib/admin-hooks';
import type { TableWithOrder } from '../../lib/types';
import { useTenant } from '../../lib/tenant-context';
import { cn } from '../../lib/utils';
import { Clock, CheckCircle2, AlertTriangle, ShoppingBag, Bike, Store, ChefHat, X, Receipt } from 'lucide-react';
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function CajaOverviewPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    stations,
    loading: stationsLoading
  } = useKitchenStations(restaurant?.id);
  const {
    orders,
    loading: ordersLoading,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder
  } = useAdminOrders(restaurant?.id);
  const {
    tables,
    refetch: refetchTables
  } = useTableManagement(restaurant?.id);
  const [updatingOrders, setUpdatingOrders] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [activeFilter, setActiveFilter] = useState<'all' | 'new' | 'preparing' | 'ready' | 'delayed'>('all');
  const [showPayModal, setShowPayModal] = useState(false);
  const [payTable, setPayTable] = useState<TableWithOrder | null>(null);
  const isCapricho = String(restaurant?.id) === '2' || restaurant?.slug === 'capricho-de-canela';
  const isCongelados = String(restaurant?.id) === '18' || restaurant?.slug === 'heladeria-congelados';
  const isNatucafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  const isSimplifiedFlow = isCapricho || isCongelados;
  const [channelFilter, setChannelFilter] = useState<'all' | 'marketplace' | 'restaurant'>('all');
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  const handleOpenPay = useCallback((order: any) => {
    if (!order?.table_id) {
      addToast('error', 'Este pedido no tiene mesa asociada.');
      return;
    }
    const matched = tables.find(t => String(t.id) === String(order.table_id));
    if (matched) {
      setPayTable(matched);
    } else {
      setPayTable({
        id: String(order.table_id),
        restaurant_id: String(restaurant?.id || ''),
        table_number: order.table_id,
        table_name: null,
        zone: null,
        capacity: null,
        qr_code_url: null,
        qr_token: null,
        is_active: true,
        created_at: '',
        updated_at: '',
        current_order: order,
        current_order_items: order.items || [],
        status: 'occupied',
        order_channel: order.source === 'waiter' ? 'waiter' : 'qr',
        time_open_minutes: null,
        item_count: (order.items || []).length
      } as TableWithOrder);
    }
    setShowPayModal(true);
  }, [tables, restaurant?.id, addToast]);
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1840">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-1841">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-1842" />
        </div>
      </AdminLayout>;
  }
  // Filter active orders
  const activeOrders = orders.filter((o: any) => !['delivered', 'cancelled', 'out_for_delivery'].includes(o.status));
  // Channel classification (NatuCafé)
  const isMarketplaceOrder = (o: any) => o.delivery_type === 'delivery' || o.delivery_type === 'pickup';
  const isRestaurantOrder = (o: any) => o.delivery_type === 'dine_in' || !isMarketplaceOrder(o) && (o.table_id || o.source === 'waiter' || o.source === 'qr');
  const marketplaceCount = activeOrders.filter(isMarketplaceOrder).length;
  const restaurantCount = activeOrders.filter(isRestaurantOrder).length;
  const channeledOrders = isNatucafe ? activeOrders.filter((o: any) => {
    if (channelFilter === 'all') return true;
    if (channelFilter === 'marketplace') return isMarketplaceOrder(o);
    if (channelFilter === 'restaurant') return isRestaurantOrder(o);
    return true;
  }) : activeOrders;
  const sortedOrders = [...channeledOrders].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  const newOrders = channeledOrders.filter(o => o.status === 'new' || o.status === 'accepted');
  const preparingOrders = channeledOrders.filter(o => o.status === 'preparing');
  const readyOrders = channeledOrders.filter(o => o.status === 'ready' || o.status === 'searching_driver');
  const delayedOrders = channeledOrders.filter(o => {
    const waitMinutes = Math.floor((new Date().getTime() - new Date(o.created_at).getTime()) / 60000);
    return waitMinutes >= 15;
  });
  const getShortOrderNumber = (orderNumber: string) => {
    if (!orderNumber) return '';
    const parts = orderNumber.split('-');
    return parts[parts.length - 1] || orderNumber;
  };
  // Action handler — now with feedback
  const handleOrderAction = async (orderId: string, action: string) => {
    setUpdatingOrders(prev => new Set(prev).add(orderId));
    let result: ActionResult = {
      success: false,
      error: 'Acción desconocida'
    };
    try {
      switch (action) {
        case 'send_to_kitchen':
          {
            // Combined: accept + start preparation in one action
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '🔥 Enviado a cocina');
            break;
          }
        case 'start_preparation':
          result = await startOrderPreparation(orderId);
          if (result.success) addToast('success', '🔥 Enviado a cocina');
          break;
        case 'mark_ready':
          result = await markOrderReady(orderId);
          if (result.success) addToast('success', '✅ Pedido marcado como listo');
          break;
        case 'deliver':
          result = await deliverOrder(orderId);
          if (result.success) addToast('success', '🚀 Pedido entregado');
          break;
        case 'capricho_receive':
          {
            // Combined: accept + start preparation
            result = await acceptOrder(orderId);
            if (result.success) {
              result = await startOrderPreparation(orderId);
            }
            if (result.success) addToast('success', '✅ Pedido recibido');
            break;
          }
        case 'capricho_complete':
          {
            // Combined: mark ready + deliver
            result = await markOrderReady(orderId);
            if (result.success) {
              result = await deliverOrder(orderId);
            }
            if (result.success) addToast('success', '🎉 Pedido completado');
            break;
          }
        case 'cancel':
          if (confirm('¿Estás seguro de cancelar este pedido?')) {
            result = await cancelOrder(orderId);
            if (result.success) addToast('success', '❌ Pedido cancelado');
          } else {
            result = {
              success: true
            };
          }
          break;
      }
      if (!result.success && result.error) {
        addToast('error', result.error);
      }
    } catch (err: any) {
      addToast('error', err?.message || 'Error inesperado');
    } finally {
      setUpdatingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-1843">
      <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans" data-id="element-1844">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-1845">
          <div data-id="element-1846">
            <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-1847">
              Control de Caja
              <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-1848">
                {activeOrders.length} activos
              </span>
            </h1>
            <p className="text-white/50 text-sm mt-1" data-id="element-1849">
              Vista general de todas las estaciones y pedidos en curso
            </p>
          </div>
        </div>

        {/* NatuCafé channel tabs */}
        {isNatucafe && <div className="mb-6 flex flex-wrap gap-2 p-1.5 bg-[#141414] border border-white/5 rounded-2xl w-full sm:w-auto" data-id="element-1850">
            {([{
          key: 'all',
          label: 'Todos',
          count: activeOrders.length,
          icon: null
        }, {
          key: 'marketplace',
          label: 'Marketplace',
          count: marketplaceCount,
          icon: Bike
        }, {
          key: 'restaurant',
          label: 'En el restaurante',
          count: restaurantCount,
          icon: Store
        }] as const).map(tab => {
          const isActive = channelFilter === tab.key;
          const Icon = tab.icon;
          return <button key={tab.key} onClick={() => setChannelFilter(tab.key)} className={cn('flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all flex-1 sm:flex-none justify-center', isActive ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white hover:bg-white/5')} data-id="element-1851">
                  {Icon && <Icon className="w-4 h-4" data-id="element-1852" />}
                  <span data-id="element-1853">{tab.label}</span>
                  <span className={cn('min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-black flex items-center justify-center tabular-nums', isActive ? 'bg-black/10 text-black' : 'bg-white/10 text-white/70')} data-id="element-1854">
                    {tab.count}
                  </span>
                </button>;
        })}
          </div>}

        {/* Stats Row — clickable filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-id="element-1855">
          <button onClick={() => setActiveFilter(activeFilter === 'new' ? 'all' : 'new')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-blue-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'new' ? 'border-blue-500 ring-2 ring-blue-500/40 bg-blue-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1856">
            <div className="flex items-center justify-between mb-2" data-id="element-1857">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-1858">
                <ShoppingBag className="w-4 h-4 text-blue-400" data-id="element-1859" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-1860">
                {newOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-1861">Pendientes</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'preparing' ? 'all' : 'preparing')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-amber-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'preparing' ? 'border-amber-500 ring-2 ring-amber-500/40 bg-amber-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1862">
            <div className="flex items-center justify-between mb-2" data-id="element-1863">
              <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-1864">
                <ChefHat className="w-4 h-4 text-amber-400" data-id="element-1865" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-1866">
                {preparingOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-1867">En Cocina</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'ready' ? 'all' : 'ready')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-emerald-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'ready' ? 'border-emerald-500 ring-2 ring-emerald-500/40 bg-emerald-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1868">
            <div className="flex items-center justify-between mb-2" data-id="element-1869">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-1870">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" data-id="element-1871" />
              </div>
              <span className="text-2xl font-black tabular-nums" data-id="element-1872">
                {readyOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-white/50" data-id="element-1873">Listos</p>
          </button>
          <button onClick={() => setActiveFilter(activeFilter === 'delayed' ? 'all' : 'delayed')} className={cn('bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-l-red-500 shadow-lg text-left cursor-pointer transition-all active:scale-[0.97]', activeFilter === 'delayed' ? 'border-red-500 ring-2 ring-red-500/40 bg-red-500/10' : 'border-white/5 hover:bg-white/[0.03]')} data-id="element-1874">
            <div className="flex items-center justify-between mb-2" data-id="element-1875">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center" data-id="element-1876">
                <AlertTriangle className="w-4 h-4 text-red-400" data-id="element-1877" />
              </div>
              <span className="text-2xl font-black tabular-nums text-red-400" data-id="element-1878">
                {delayedOrders.length}
              </span>
            </div>
            <p className="text-sm font-bold text-red-400/70" data-id="element-1879">
              Atrasados (&gt;15m)
            </p>
          </button>
        </div>

        {/* Active filter indicator */}
        {activeFilter !== 'all' && <div className="flex items-center gap-2 mb-4" data-id="element-1880">
            <span className="text-sm text-white/50" data-id="element-1881">Filtrando por:</span>
            <span className={cn('px-3 py-1 rounded-full text-xs font-bold', activeFilter === 'new' && 'bg-blue-500/20 text-blue-400', activeFilter === 'preparing' && 'bg-amber-500/20 text-amber-400', activeFilter === 'ready' && 'bg-emerald-500/20 text-emerald-400', activeFilter === 'delayed' && 'bg-red-500/20 text-red-400')} data-id="element-1882">
              {activeFilter === 'new' && `Pendientes (${newOrders.length})`}
              {activeFilter === 'preparing' && `En Cocina (${preparingOrders.length})`}
              {activeFilter === 'ready' && `Listos (${readyOrders.length})`}
              {activeFilter === 'delayed' && `Atrasados (${delayedOrders.length})`}
            </span>
            <button onClick={() => setActiveFilter('all')} className="text-white/30 hover:text-white/60 transition-colors" data-id="element-1883">
              <X className="w-4 h-4" data-id="element-1884" />
            </button>
          </div>}

        {/* Orders Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 items-start" data-id="element-1885">
          {ordersLoading || stationsLoading ? [1, 2, 3, 4].map(i => <div key={i} className="h-64 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-1886" />) : (() => {
          const filteredOrders = sortedOrders.filter((order: any) => {
            if (activeFilter === 'all') return true;
            if (activeFilter === 'new') return order.status === 'new' || order.status === 'accepted';
            if (activeFilter === 'preparing') return order.status === 'preparing';
            if (activeFilter === 'ready') return order.status === 'ready' || order.status === 'searching_driver';
            if (activeFilter === 'delayed') {
              const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
              return waitMinutes >= 15;
            }
            return true;
          });
          return filteredOrders.length > 0 ? filteredOrders.map((order: any) => {
            const isUpdatingOrder = updatingOrders.has(order.id);
            const waitMinutes = Math.floor((new Date().getTime() - new Date(order.created_at).getTime()) / 60000);
            const stationProgress = new Map<string, {
              total: number;
              ready: number;
              name: string;
            }>();
            order.items.forEach((item: any) => {
              const stationCode = item.station_code || 'sin_asignar';
              const stationName = stations.find(s => s.code === stationCode)?.name || (stationCode === 'sin_asignar' ? 'General' : stationCode);
              if (!stationProgress.has(stationCode)) {
                stationProgress.set(stationCode, {
                  total: 0,
                  ready: 0,
                  name: stationName
                });
              }
              const progress = stationProgress.get(stationCode)!;
              progress.total += item.quantity;
              if (item.status === 'ready' || item.status === 'served') {
                progress.ready += item.quantity;
              }
            });
            const allItemsReady = Array.from(stationProgress.values()).every(p => p.ready === p.total) && order.items.length > 0;
            // Calculate overall progress percentage
            const totalItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.total, 0);
            const readyItems = Array.from(stationProgress.values()).reduce((sum, p) => sum + p.ready, 0);
            const overallPercentage = totalItems > 0 ? Math.round(readyItems / totalItems * 100) : 0;
            // Kitchen activity: any item has been touched by KDS
            // (waiter/dine_in orders skip 'preparing' status — items get
            //  worked on while order.status stays 'new')
            const hasKitchenActivity = order.items.some((it: any) => it.status === 'ready' || it.status === 'served' || it.status === 'in_progress' || it.status === 'preparing');
            const showProgressView = order.status === 'preparing' || order.status === 'ready' || hasKitchenActivity;
            let borderColor = 'border-white/10';
            if (order.status === 'new' || order.status === 'accepted') borderColor = 'border-blue-500';else if (order.status === 'preparing') borderColor = 'border-amber-500';else if (order.status === 'ready' || order.status === 'searching_driver' || allItemsReady) borderColor = 'border-emerald-500';
            // ── Simplified card (Capricho / Congelados) ──
            if (isSimplifiedFlow) {
              const caprichoStatus = order.status === 'new' || order.status === 'accepted' ? 'nuevo' : order.status === 'preparing' ? 'en_preparacion' : order.status === 'ready' || order.status === 'searching_driver' ? 'listo' : 'otro';
              return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', caprichoStatus === 'nuevo' ? 'border-blue-500' : caprichoStatus === 'en_preparacion' ? 'border-amber-500' : caprichoStatus === 'listo' ? 'border-emerald-500' : 'border-white/10')} data-id="element-1887">
                          {/* Header */}
                          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]" data-id="element-1888">
                            <div className="flex items-center gap-2" data-id="element-1889">
                              <span className="text-lg font-black text-white tabular-nums" data-id="element-1890">
                                #{getShortOrderNumber(order.order_number)}
                              </span>
                              {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1891">
                                  <Bike className="w-3 h-3" data-id="element-1892" /> Delivery
                                </span> : <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1893">
                                  <ShoppingBag className="w-3 h-3" data-id="element-1894" /> Llevar
                                </span>}
                            </div>
                            <span className={cn('text-xl font-black tabular-nums', waitMinutes >= 15 ? 'text-red-500' : waitMinutes >= 10 ? 'text-amber-500' : 'text-emerald-500')} data-id="element-1895">
                              {waitMinutes}m
                            </span>
                          </div>

                          {/* Customer */}
                          <div className="px-4 py-3 border-b border-white/5" data-id="element-1896">
                            <p className="text-base font-black text-white" data-id="element-1897">
                              {order.customer_name}
                            </p>
                            {order.customer_phone && <p className="text-xs text-white/40 mt-0.5" data-id="element-1898">
                                {order.customer_phone}
                              </p>}
                            <p className="text-sm font-black text-[#C8A96A] mt-1" data-id="element-1899">
                              ₡{order.total.toLocaleString()}
                            </p>
                          </div>

                          {/* Status */}
                          <div className={cn('px-4 py-2.5 border-b', caprichoStatus === 'nuevo' ? 'bg-blue-500/10 border-blue-500/20' : caprichoStatus === 'en_preparacion' ? 'bg-amber-500/10 border-amber-500/20' : caprichoStatus === 'listo' ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-white/5 border-white/5')} data-id="element-1900">
                            <span className={cn('text-xs font-black uppercase tracking-wider flex items-center gap-1.5', caprichoStatus === 'nuevo' ? 'text-blue-400' : caprichoStatus === 'en_preparacion' ? 'text-amber-400' : caprichoStatus === 'listo' ? 'text-emerald-400' : 'text-white/40')} data-id="element-1901">
                              {caprichoStatus === 'nuevo' && <>
                                  <Clock className="w-3.5 h-3.5" data-id="element-1902" /> Nuevo pedido
                                </>}
                              {caprichoStatus === 'en_preparacion' && <>
                                  <ChefHat className="w-3.5 h-3.5" data-id="element-1903" /> En
                                  preparación
                                </>}
                              {caprichoStatus === 'listo' && <>
                                  <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1904" /> Listo
                                  para entregar
                                </>}
                            </span>
                          </div>

                          {/* Items with toppings */}
                          <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-1905">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-1906">
                              Detalle del pedido
                            </h4>
                            <div className="space-y-2.5" data-id="element-1907">
                              {order.items.map((item: any) => <div key={item.id} data-id="element-1908">
                                  <div className="flex justify-between text-sm" data-id="element-1909">
                                    <span className="text-white font-bold" data-id="element-1910">
                                      {item.quantity}x {item.item_name_snapshot}
                                    </span>
                                    <span className="text-[#C8A96A] font-bold text-xs shrink-0" data-id="element-1911">
                                      ₡
                                      {(item.unit_price * item.quantity).toLocaleString()}
                                    </span>
                                  </div>
                                  {item.options && item.options.length > 0 && <div className="mt-1 ml-4 space-y-0.5" data-id="element-1912">
                                      {item.options.map((opt: any, idx: number) => <div key={idx} className="flex justify-between text-xs" data-id="element-1913">
                                            <span className="text-amber-400/80" data-id="element-1914">
                                              + {opt.option_name}
                                            </span>
                                            {opt.price_delta > 0 && <span className="text-white/30" data-id="element-1915">
                                                +₡
                                                {opt.price_delta.toLocaleString()}
                                              </span>}
                                          </div>)}
                                    </div>}
                                  {item.notes && <p className="text-xs text-white/30 italic mt-1 ml-4" data-id="element-1916">
                                      📝 {item.notes}
                                    </p>}
                                </div>)}
                            </div>
                            {order.notes && <div className="mt-3 pt-3 border-t border-white/5" data-id="element-1917">
                                <p className="text-xs text-white/40 italic" data-id="element-1918">
                                  📝 Nota: {order.notes}
                                </p>
                              </div>}
                          </div>

                          {/* Actions */}
                          <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-1919">
                            {caprichoStatus === 'nuevo' && <button onClick={() => handleOrderAction(order.id, 'capricho_receive')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-[#8B5E3C] hover:bg-[#A0704A] text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-1920">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1921">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1922" />
                                    Procesando...
                                  </span> : '✅ PEDIDO RECIBIDO'}
                              </button>}
                            {caprichoStatus === 'en_preparacion' && <button onClick={() => handleOrderAction(order.id, 'capricho_complete')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-1923">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1924">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1925" />
                                    Procesando...
                                  </span> : '🎉 PEDIDO COMPLETADO'}
                              </button>}
                            {caprichoStatus === 'listo' && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-all disabled:opacity-50" data-id="element-1926">
                                {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1927">
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1928" />
                                    Procesando...
                                  </span> : '🚀 ENTREGAR'}
                              </button>}
                            {order.delivery_type === 'dine_in' && <button onClick={() => handleOpenPay(order)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1929">
                                <Receipt className="w-4 h-4" data-id="element-1930" />
                                COBRAR / FACTURAR
                              </button>}
                            {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-1931">
                                Cancelar pedido
                              </button>}
                          </div>
                        </div>;
            }
            // ── Standard card (non-Capricho) ──
            return <div key={order.id} className={cn('bg-[#141414] rounded-2xl flex flex-col border-l-4 border-y border-r border-white/5 overflow-hidden shadow-xl transition-all', borderColor, waitMinutes >= 15 && order.status !== 'ready' ? 'shadow-[0_0_15px_rgba(239,68,68,0.15)]' : '')} data-id="element-1932">
                        {/* Order Header */}
                        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between gap-2 bg-white/[0.02]" data-id="element-1933">
                          <div className="flex items-center gap-2 min-w-0" data-id="element-1934">
                            <span className="text-lg font-black text-white tabular-nums" data-id="element-1935">
                              #{getShortOrderNumber(order.order_number)}
                            </span>
                            {order.delivery_type === 'delivery' ? <span className="bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1936">
                                <Bike className="w-3 h-3" data-id="element-1937" /> Delivery
                              </span> : order.delivery_type === 'pickup' ? <span className="bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1938">
                                <ShoppingBag className="w-3 h-3" data-id="element-1939" /> Llevar
                              </span> : <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded-md text-xs font-bold flex items-center gap-1" data-id="element-1940">
                                <Store className="w-3 h-3" data-id="element-1941" /> Mesa{' '}
                                {order.table_id || ''}
                              </span>}
                          </div>
                          <span className={cn('text-xl font-black tabular-nums tracking-tighter shrink-0', waitMinutes >= 15 && order.status !== 'ready' ? 'text-red-500 animate-pulse' : waitMinutes >= 10 && order.status !== 'ready' ? 'text-amber-500' : 'text-emerald-500')} data-id="element-1942">
                            {waitMinutes}m
                          </span>
                        </div>

                        {/* Customer & Total */}
                        <div className="px-4 py-2 border-b border-white/5 flex justify-between items-center" data-id="element-1943">
                          <p className="text-sm text-white/70 font-bold truncate pr-2" data-id="element-1944">
                            {order.customer_name}
                          </p>
                          <p className="text-sm font-black text-[#F4D35E] shrink-0" data-id="element-1945">
                            ₡{order.total.toLocaleString()}
                          </p>
                        </div>

                        {/* Status Banner — shows current phase */}
                        {(order.status === 'preparing' || hasKitchenActivity && order.status !== 'ready') && <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20" data-id="element-1946">
                            <div className="flex items-center justify-between mb-2" data-id="element-1947">
                              <span className="text-xs font-black text-amber-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1948">
                                <ChefHat className="w-3.5 h-3.5" data-id="element-1949" />
                                {allItemsReady ? 'Cocina terminó' : 'En cocina'}
                              </span>
                              <span className="text-sm font-black text-amber-400 tabular-nums" data-id="element-1950">
                                {overallPercentage}%
                              </span>
                            </div>
                            <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1951">
                              <div className={cn('h-full rounded-full transition-all duration-700 ease-out', overallPercentage === 100 ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                    width: `${overallPercentage}%`
                  }} data-id="element-1952" />
                            </div>
                          </div>}

                        {(order.status === 'new' || order.status === 'accepted') && !hasKitchenActivity && <div className="px-4 py-3 bg-blue-500/10 border-b border-blue-500/20" data-id="element-1953">
                              <span className="text-xs font-black text-blue-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1954">
                                <Clock className="w-3.5 h-3.5" data-id="element-1955" />
                                Nuevo pedido — Pendiente de enviar a cocina
                              </span>
                            </div>}

                        {order.status === 'ready' && <div className="px-4 py-3 bg-emerald-500/10 border-b border-emerald-500/20" data-id="element-1956">
                            <span className="text-xs font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5" data-id="element-1957">
                              <CheckCircle2 className="w-3.5 h-3.5" data-id="element-1958" />
                              ¡Listo para entregar!
                            </span>
                          </div>}

                        {/* Station Progress — show when preparing, ready, or any kitchen activity */}
                        {showProgressView && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-1959">
                            <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-1960">
                              Progreso por estación
                            </h4>
                            {stationProgress.size === 0 ? <p className="text-sm text-white/40 italic" data-id="element-1961">
                                Sin items
                              </p> : <div className="space-y-3" data-id="element-1962">
                                {Array.from(stationProgress.entries()).map(([code, progress]) => {
                    const isComplete = progress.ready === progress.total;
                    const percentage = Math.round(progress.ready / progress.total * 100);
                    return <div key={code} className="space-y-1.5" data-id="element-1963">
                                        <div className="flex justify-between items-center text-sm" data-id="element-1964">
                                          <span className={cn('font-bold truncate pr-2', isComplete ? 'text-emerald-400' : 'text-white/80')} data-id="element-1965">
                                            {isComplete ? '✅' : '🔥'}{' '}
                                            {progress.name}
                                          </span>
                                          <span className={cn('font-black tabular-nums shrink-0', isComplete ? 'text-emerald-400' : 'text-amber-400')} data-id="element-1966">
                                            {progress.ready} / {progress.total}
                                          </span>
                                        </div>
                                        <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden" data-id="element-1967">
                                          <div className={cn('h-full rounded-full transition-all duration-500', isComplete ? 'bg-emerald-500' : 'bg-amber-500')} style={{
                          width: `${percentage}%`
                        }} data-id="element-1968" />
                                        </div>
                                      </div>;
                  })}
                              </div>}
                          </div>}

                        {/* For new/accepted orders without kitchen activity, show items list */}
                        {(order.status === 'new' || order.status === 'accepted') && !hasKitchenActivity && <div className="p-4 flex-1 bg-[#0B0B0B]/50" data-id="element-1969">
                              <h4 className="text-xs font-black text-white/30 uppercase tracking-wider mb-3" data-id="element-1970">
                                Items del pedido
                              </h4>
                              <div className="space-y-1.5" data-id="element-1971">
                                {order.items.map((item: any) => <div key={item.id} className="flex justify-between text-sm" data-id="element-1972">
                                    <span className="text-white/70 truncate pr-2" data-id="element-1973">
                                      {item.quantity}x {item.item_name_snapshot}
                                    </span>
                                    <span className="text-white/40 text-xs shrink-0" data-id="element-1974">
                                      {stations.find(s => s.code === item.station_code)?.name || 'General'}
                                    </span>
                                  </div>)}
                              </div>
                            </div>}

                        {/* Action Footer */}
                        <div className="p-3 border-t border-white/5 bg-[#141414]" data-id="element-1975">
                          {(order.status === 'new' || order.status === 'accepted') && <button onClick={() => handleOrderAction(order.id, order.status === 'new' ? 'send_to_kitchen' : 'start_preparation')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-amber-500 hover:bg-amber-400 text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1976">
                              {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1977">
                                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-1978" />
                                  Enviando a cocina...
                                </span> : '🔥 ENVIAR A COCINA'}
                            </button>}
                          {order.status === 'preparing' && <button onClick={() => handleOrderAction(order.id, 'mark_ready')} disabled={isUpdatingOrder || !allItemsReady} className={cn('w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all', allItemsReady ? 'bg-emerald-500 hover:bg-emerald-400 text-black active:scale-95 shadow-[0_0_15px_rgba(16,185,129,0.2)]' : 'bg-white/5 text-white/30 cursor-not-allowed')} data-id="element-1979">
                              {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1980">
                                  <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" data-id="element-1981" />
                                  Procesando...
                                </span> : allItemsReady ? '✅ MARCAR LISTO' : <span className="flex items-center gap-2" data-id="element-1982">
                                  <span className="w-4 h-4 border-2 border-white/20 border-t-amber-400 rounded-full animate-spin" data-id="element-1983" />
                                  EN COCINA — {overallPercentage}% LISTO
                                </span>}
                            </button>}
                          {(order.status === 'ready' || order.status === 'searching_driver') && <button onClick={() => handleOrderAction(order.id, 'deliver')} disabled={isUpdatingOrder} className="w-full h-12 rounded-xl text-sm font-black bg-emerald-600 hover:bg-emerald-500 text-white flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1984">
                              {isUpdatingOrder ? <span className="flex items-center gap-2" data-id="element-1985">
                                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1986" />
                                  Procesando...
                                </span> : order.delivery_type === 'delivery' ? '🚀 MARCAR COMO ENVIADO' : '🚀 ENTREGAR AL CLIENTE'}
                            </button>}
                          {order.delivery_type === 'dine_in' && <button onClick={() => handleOpenPay(order)} disabled={isUpdatingOrder} className="w-full mt-2 h-11 rounded-xl text-sm font-black bg-[#F4D35E] hover:bg-[#FFE08A] text-black flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50" data-id="element-1987">
                              <Receipt className="w-4 h-4" data-id="element-1988" />
                              COBRAR / FACTURAR
                            </button>}
                          {['new', 'accepted', 'preparing'].includes(order.status) && <button onClick={() => handleOrderAction(order.id, 'cancel')} disabled={isUpdatingOrder} className="w-full text-center text-[10px] font-bold text-white/20 hover:text-red-500 transition-colors mt-2 uppercase tracking-wider" data-id="element-1989">
                              Cancelar pedido
                            </button>}
                        </div>
                      </div>;
          }) : <div className="col-span-full py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-1990">
                    <CheckCircle2 className="w-16 h-16 text-white/10 mb-4" data-id="element-1991" />
                    <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-1992">
                      {activeFilter !== 'all' ? 'Sin pedidos en esta categoría' : 'Todo bajo control'}
                    </h3>
                    <p className="text-sm" data-id="element-1993">
                      {activeFilter !== 'all' ? 'No hay pedidos que coincidan con este filtro.' : 'No hay pedidos activos en este momento.'}
                    </p>
                    {activeFilter !== 'all' && <button onClick={() => setActiveFilter('all')} className="mt-4 px-4 py-2 rounded-lg bg-white/10 text-white/60 text-sm font-bold hover:bg-white/15 transition-colors" data-id="element-1994">
                        Ver todos los pedidos
                      </button>}
                  </div>;
        })()}
        </div>

        {/* PayTableModal */}
        <PayTableModal isOpen={showPayModal} onClose={() => {
        setShowPayModal(false);
        setPayTable(null);
      }} table={payTable} restaurantId={restaurant?.id} onSuccess={allPaid => {
        refetchTables();
        if (allPaid && payTable?.current_order?.id) {
          // Mark the order as delivered when fully paid
          deliverOrder(payTable.current_order.id).then(() => {
            addToast('success', '✅ Pedido cobrado y cerrado');
          });
        } else {
          addToast('success', '💵 Pago parcial registrado');
        }
      }} createdBy={tenantCtx?.userId || null} data-id="element-1995" />

        {/* ═══ TOAST NOTIFICATIONS ═══ */}
        {toasts.length > 0 && <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-1996">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-1997">
                <span className="flex-1" data-id="element-1998">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-1999">
                  <X className="w-4 h-4" data-id="element-2000" />
                </button>
              </div>)}
          </div>}
      </div>
    </AdminLayout>;
}